@if (total() === 0) {
  <div class="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-neutral-200 py-14 text-center text-neutral-400">
    <mat-icon class="text-4xl!">task_alt</mat-icon>
    <p class="text-base">{{ "guaranteeV2.message.queueEmpty" | translate }}</p>
  </div>
} @else {
  <div class="flex flex-col gap-3">
    <!-- The order is the point of the list, so it is stated rather than left to be inferred. -->
    <div class="flex flex-wrap items-center gap-2 px-1 text-sm text-neutral-500">
      <mat-icon
        class="h-4! w-4! text-base! leading-4! text-neutral-300"
        [matTooltip]="'guaranteeV2.message.queueOrderHint' | translate"
        >sort</mat-icon
      >
      <span>{{ "guaranteeV2.message.queueOrder" | translate }}</span>
      <span
        class="ml-auto font-semibold text-neutral-600 tabular-nums"
        [matTooltip]="'guaranteeV2.message.queueTotalHint' | translate"
        >{{ "guaranteeV2.label.awaitingDecision" | translate: { count: pendingCount() } }}</span
      >
    </div>

    @if (queue().ready.length > 0) {
      <section class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-2 px-1">
          <span
            class="text-sm font-semibold text-neutral-500"
            [matTooltip]="'guaranteeV2.message.queueReadyHint' | translate"
            >{{ "guaranteeV2.label.queueReady" | translate }}</span
          >
          <guarantees-chip variant="outline">{{ queue().ready.length }}</guarantees-chip>
        </div>

        @for (row of queue().ready; track row.key) {
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-neutral-200 bg-neutral-0 px-2 py-1.5">
            <span class="inline-flex shrink-0 items-center gap-1 text-sm text-neutral-500 tabular-nums">
              <span class="font-medium text-neutral-700">{{ row.visitDate | date: "d MMM yyyy" }}</span>
              @if (row.waitingDays > 0) {
                <guarantees-chip
                  tone="warning"
                  icon="hourglass_bottom"
                  [tooltip]="'guaranteeV2.message.waitingDaysHint' | translate: { count: row.waitingDays }"
                  >{{ row.waitingDays }}{{ "guaranteeV2.label.daysShort" | translate }}</guarantees-chip
                >
              }
            </span>

            <mat-icon
              class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
              [matTooltip]="kindLabelKeys[row.set.kind] | translate"
              >{{ kindIcons[row.set.kind] }}</mat-icon
            >

            <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-800">{{ row.set.name }}</span>

            <!-- The number is what this row would rule on, which in a course is rarely all of it. -->
            @if (row.set.isSet) {
              <guarantees-chip
                class="shrink-0"
                variant="outline"
                [tooltip]="'guaranteeV2.message.undecidedHint' | translate: { count: row.pendingLines.length }"
                >&times;{{ row.pendingLines.length }}</guarantees-chip
              >
            }

            @if (row.diagnosis; as diagnosis) {
              <span class="max-w-64 min-w-0 shrink-0">
                <guarantees-diagnosis-chip
                  [diagnosis]="diagnosis"
                  [showName]="false"
                  [showCaseState]="false"
                ></guarantees-diagnosis-chip>
              </span>
            }

            @if (row.partnerName) {
              <span class="hidden max-w-40 shrink-0 truncate text-sm text-neutral-400 lg:inline">{{ row.partnerName }}</span>
            }

            @if (row.breachCount > 0) {
              <guarantees-chip
                class="shrink-0"
                icon="rule"
                [tone]="row.maxBreachSeverity ? severityTones[row.maxBreachSeverity] : 'neutral'"
                [tooltip]="'guaranteeV2.message.lineBreachHint' | translate: { count: row.breachCount }"
                >{{ row.breachCount }}</guarantees-chip
              >
            }

            <span class="ml-auto flex shrink-0 items-center gap-1">
              @if (!readOnly()) {
                <!-- The answer is chosen here; the flow that opens only collects the grounds for it. -->
                <button
                  class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-semibold text-success-700 transition-colors duration-150 hover:bg-success-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none"
                  type="button"
                  [disabled]="busy(row.guaranteeId)"
                  [matTooltip]="'guaranteeV2.message.guaranteeLineHint' | translate"
                  (click)="rule(row, decisions.Guaranteed)"
                >
                  <mat-icon class="h-4! w-4! text-base! leading-4!">check_circle</mat-icon>
                  <span class="hidden sm:inline">{{ "guaranteeV2.action.guaranteeLine" | translate }}</span>
                </button>
                <button
                  class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-semibold text-error-700 transition-colors duration-150 hover:bg-error-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none"
                  type="button"
                  [disabled]="busy(row.guaranteeId)"
                  [matTooltip]="'guaranteeV2.message.declineLineHint' | translate"
                  (click)="rule(row, decisions.NotGuaranteed)"
                >
                  <mat-icon class="h-4! w-4! text-base! leading-4!">block</mat-icon>
                  <span class="hidden sm:inline">{{ "guaranteeV2.action.declineLine" | translate }}</span>
                </button>
              }
              <button
                class="inline-flex size-7 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none sm:size-6"
                type="button"
                [attr.aria-label]="'guaranteeV2.message.openInStreamHint' | translate"
                [matTooltip]="'guaranteeV2.message.openInStreamHint' | translate"
                (click)="guaranteeOpened.emit(row.guaranteeId)"
              >
                <mat-icon class="h-4! w-4! text-base! leading-4!">open_in_new</mat-icon>
              </button>
            </span>
          </div>
        }
      </section>
    }

    <!--
      Held apart rather than mixed in: the backend refuses a ruling on a line with no diagnosis, so
      these are a different job — attach one in the care plan, then they join the list above.
    -->
    @if (queue().blocked.length > 0) {
      <section class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-2 px-1">
          <mat-icon
            class="h-4! w-4! text-base! leading-4! text-warning-500"
            [matTooltip]="'guaranteeV2.message.queueBlockedHint' | translate"
            >error_outline</mat-icon
          >
          <span class="text-sm font-semibold text-neutral-500">{{ "guaranteeV2.label.queueBlocked" | translate }}</span>
          <guarantees-chip variant="outline">{{ queue().blocked.length }}</guarantees-chip>
        </div>

        @for (row of queue().blocked; track row.key) {
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-warning-200 bg-warning-50/40 px-2 py-1.5">
            <span class="inline-flex shrink-0 items-center gap-1 text-sm text-neutral-500 tabular-nums">
              <span class="font-medium text-neutral-700">{{ row.visitDate | date: "d MMM yyyy" }}</span>
              @if (row.waitingDays > 0) {
                <guarantees-chip
                  tone="warning"
                  icon="hourglass_bottom"
                  [tooltip]="'guaranteeV2.message.waitingDaysHint' | translate: { count: row.waitingDays }"
                  >{{ row.waitingDays }}{{ "guaranteeV2.label.daysShort" | translate }}</guarantees-chip
                >
              }
            </span>

            <mat-icon
              class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
              [matTooltip]="kindLabelKeys[row.set.kind] | translate"
              >{{ kindIcons[row.set.kind] }}</mat-icon
            >

            <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-800">{{ row.set.name }}</span>

            <guarantees-chip
              class="shrink-0"
              tone="warning"
              icon="link_off"
              [tooltip]="'guaranteeV2.message.lineWithoutDiagnosisHint' | translate"
              >{{ "guaranteeV2.label.noDiagnosis" | translate }}</guarantees-chip
            >

            <span class="ml-auto flex shrink-0 items-center gap-1">
              <!--
                Refusing stays open where granting is blocked: every ground a grant is refused on is
                a ground the insurer is not committed, which refusing states rather than contradicts.
                Granting is what the missing diagnosis rules out, and that is the work above.
              -->
              @if (!readOnly()) {
                <button
                  class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm font-semibold text-error-700 transition-colors duration-150 hover:bg-error-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none"
                  type="button"
                  [disabled]="busy(row.guaranteeId)"
                  [matTooltip]="'guaranteeV2.message.declineLineHint' | translate"
                  (click)="rule(row, decisions.NotGuaranteed)"
                >
                  <mat-icon class="h-4! w-4! text-base! leading-4!">block</mat-icon>
                  <span class="hidden sm:inline">{{ "guaranteeV2.action.declineLine" | translate }}</span>
                </button>
              }

              <button
                class="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-sm font-semibold text-primary-700 hover:bg-primary-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                type="button"
                [matTooltip]="'guaranteeV2.message.openInStreamHint' | translate"
                (click)="guaranteeOpened.emit(row.guaranteeId)"
              >
                <mat-icon class="h-4! w-4! text-base! leading-4!">open_in_new</mat-icon>
                <span class="hidden sm:inline">{{ "guaranteeV2.action.openInStream" | translate }}</span>
              </button>
            </span>
          </div>
        }
      </section>
    }
  </div>
}
